/**
 * Major Analysis — all the maths behind Rounds → (a major) → Analysis.
 *
 * Pure functions over GET /api/tournaments/:id/analysis plus the leaderboard's
 * finishing order (utils/majorLeaderboard), so the default order everywhere is
 * the tournament rank and every filter is instant.
 *
 * Ground rules:
 *   • A player's rounds are their PERSONAL rounds (R1, R2, …) in play order —
 *     the same order the leaderboard reads (scheduled majors pass it in, legacy
 *     majors go by date then round).
 *   • Baselines (field averages, positions) always use the whole competing
 *     field; filters only choose what is shown.
 *   • Stroke index isn't stored, so a round's handicap is spread evenly over
 *     its holes wherever net is shown per hole. Birdies, putts, GIR are gross.
 *   • Nothing is estimated: a hole-level view needs the hole data, and
 *     missingData() says exactly what to add when it isn't there.
 */

import { scoreKind } from './holeScores';
import { Standing, StandingStatus, withCompetitionRanks } from './majorLeaderboard';

export type Metric = 'net' | 'gross';
type Cell = number | null;

// ── API shapes ───────────────────────────────────────────────────────────────

export interface AnalysisRound {
  round_id: number;
  date: string;
  number_of_holes: number;
  course_id: number | null;
  course_name: string;
  facility_name: string | null;
  course_teebox_id: number | null;
  course_teebox_name: string | null;
  par: number | null;
  rating: number | null;
  slope: number | null;
  hole_pars: Cell[];
  par_source: 'teebox' | 'card' | null;
}

export interface AnalysisScore {
  round_id: number;
  player_id: number;
  first_name: string;
  last_name: string;
  is_guest: boolean;
  is_disqualified: boolean;
  dq_reason: string | null;
  gross: number | null;
  handicap: number;
  net: number | null;
  penalty_strokes: number;
  penalty_reason: string | null;
  holes: Cell[];
  putts: Cell[];
  penalties: Cell[];
}

export interface AnalysisData {
  tournament: { tournament_id: number; tournament_name: string; tournament_status?: string; start_date?: string; end_date?: string };
  rounds: AnalysisRound[];
  scores: AnalysisScore[];
}

// ── Model ────────────────────────────────────────────────────────────────────

export interface CourseInfo {
  key: string;
  course_name: string;
  facility_name: string | null;
  teebox_name: string | null;
  /** "Versant #2 · Cuivre" */
  label: string;
  par: number | null;
  rating: number | null;
  slope: number | null;
  /** Pars per hole (tee box, else a saved scorecard); null where unknown. */
  hole_pars: Cell[];
  hasHolePars: boolean;
  round_ids: number[];
}

export interface PlayerRound {
  player_id: number;
  /** Personal round number, 1-based. */
  index: number;
  round: AnalysisRound;
  course: CourseInfo;
  score: AnalysisScore;
  /** Holes played, in order (1–18, or the nine). */
  played: number[];
  pars: Cell[];
  gross: number;
  net: number;
  handicap: number;
  holesFilled: number;
  holesComplete: boolean;
  /** Complete, but the holes don't add up to the entered gross. */
  holesMismatch: boolean;
  holesSum: number | null;
  hasPutts: boolean;
  hasPenalties: boolean;
}

export interface PlayerModel {
  player_id: number;
  name: string;
  first_name: string;
  last_name: string;
  rank: number | null;
  status: StandingStatus;
  /** Position in the finishing order (0 = winner) — also the colour index. */
  order: number;
  rounds: PlayerRound[];
}

export interface AnalysisModel {
  tournament: AnalysisData['tournament'];
  courses: CourseInfo[];
  players: PlayerModel[];
  maxRounds: number;
}

const courseKeyOf = (r: AnalysisRound) =>
  r.course_teebox_id != null ? `t${r.course_teebox_id}` : `c${r.course_id ?? r.round_id}`;

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const sumOf = (vals: number[]) => vals.reduce((a, v) => a + v, 0);
const fullName = (s: { first_name: string; last_name: string }) => `${s.first_name} ${s.last_name}`;

/** Holes a round covers: all 18, or the nine the scores are on. */
function playedHoles(numberOfHoles: number, holeRows: Cell[][]): number[] {
  const all = Array.from({ length: 18 }, (_, i) => i + 1);
  if (Number(numberOfHoles) !== 9) return all;
  let front = 0;
  let back = 0;
  for (const row of holeRows) {
    for (let i = 0; i < row.length; i++) {
      if (!isNum(row[i])) continue;
      if (i < 9) front += 1; else back += 1;
    }
  }
  return back > front ? all.slice(9) : all.slice(0, 9);
}

export interface BuildOptions {
  /**
   * Scheduled majors: each player's released round_ids in personal-round
   * order (the leaderboard's gated order). Only these rounds are analysed.
   * Null for legacy majors (every round, date then round order).
   */
  roundOrder?: Map<number, number[]> | null;
}

export function buildModel(data: AnalysisData, standings: Standing[], opts: BuildOptions = {}): AnalysisModel {
  const roundOrder = opts.roundOrder ?? null;
  const releasedRounds = roundOrder ? new Set(Array.from(roundOrder.values()).flat()) : null;
  const roundsById = new Map(data.rounds.map((r) => [r.round_id, r]));

  const courses = new Map<string, CourseInfo>();
  for (const r of data.rounds) {
    const key = courseKeyOf(r);
    let c = courses.get(key);
    if (!c) {
      c = {
        key,
        course_name: r.course_name,
        facility_name: r.facility_name,
        teebox_name: r.course_teebox_name,
        label: r.course_teebox_name ? `${r.course_name} · ${r.course_teebox_name}` : r.course_name,
        par: r.par,
        rating: r.rating,
        slope: r.slope,
        hole_pars: [...r.hole_pars],
        hasHolePars: false,
        round_ids: [],
      };
      courses.set(key, c);
    }
    // Several matches on one tee box: any saved card can fill a missing par.
    c.hole_pars = c.hole_pars.map((p, i) => (p != null ? p : r.hole_pars[i] ?? null));
    c.round_ids.push(r.round_id);
  }

  const holesByRound = new Map<number, Cell[][]>();
  for (const s of data.scores) {
    if (!holesByRound.has(s.round_id)) holesByRound.set(s.round_id, []);
    holesByRound.get(s.round_id)!.push(s.holes);
  }

  const toPlayerRound = (s: AnalysisScore, index: number): PlayerRound | null => {
    const round = roundsById.get(s.round_id);
    if (!round || !isNum(s.gross)) return null;
    const course = courses.get(courseKeyOf(round))!;
    const played = playedHoles(round.number_of_holes, holesByRound.get(s.round_id) ?? []);
    const pars = course.hole_pars;
    const filled = played.filter((h) => isNum(s.holes[h - 1]));
    const holesComplete = filled.length === played.length;
    const holesSum = holesComplete ? sumOf(played.map((h) => s.holes[h - 1] as number)) : null;
    return {
      player_id: s.player_id,
      index,
      round,
      course,
      score: s,
      played,
      pars,
      gross: s.gross,
      net: isNum(s.net) ? s.net : s.gross + (s.handicap || 0) + (s.penalty_strokes || 0),
      handicap: s.handicap || 0,
      holesFilled: filled.length,
      holesComplete,
      holesMismatch: holesSum != null && holesSum !== s.gross,
      holesSum,
      hasPutts: played.some((h) => isNum(s.putts[h - 1])),
      hasPenalties: played.some((h) => isNum(s.penalties[h - 1])),
    };
  };

  const byPlayer = new Map<number, AnalysisScore[]>();
  for (const s of data.scores) {
    if (releasedRounds && !releasedRounds.has(s.round_id)) continue;
    if (!byPlayer.has(s.player_id)) byPlayer.set(s.player_id, []);
    byPlayer.get(s.player_id)!.push(s);
  }
  const playOrder = (a: AnalysisScore, b: AnalysisScore) => {
    const ra = roundsById.get(a.round_id);
    const rb = roundsById.get(b.round_id);
    return String(ra?.date ?? '').localeCompare(String(rb?.date ?? '')) || a.round_id - b.round_id;
  };

  const personalRounds = (playerId: number, isGuest: boolean): PlayerRound[] => {
    const scores = byPlayer.get(playerId) ?? [];
    let ordered: AnalysisScore[];
    const order = roundOrder?.get(playerId);
    if (order) {
      ordered = order.map((rid) => scores.find((s) => s.round_id === rid)).filter((s): s is AnalysisScore => !!s);
    } else {
      const sorted = [...scores].sort(playOrder);
      // A competitor's guest matches don't count as their rounds.
      ordered = isGuest ? sorted : sorted.filter((s) => !s.is_guest);
    }
    return ordered.map((s, i) => toPlayerRound(s, i + 1)).filter((r): r is PlayerRound => !!r);
  };

  const players: PlayerModel[] = [];
  const seen = new Set<number>();
  const idByName = new Map<string, number>();
  data.scores.forEach((s) => idByName.set(fullName(s), s.player_id));
  const nameById = new Map<number, AnalysisScore>();
  data.scores.forEach((s) => nameById.set(s.player_id, s));

  for (const st of standings) {
    const pid = st.player_id ?? idByName.get(st.name);
    if (pid == null || seen.has(pid) || !nameById.has(pid)) continue;
    const s = nameById.get(pid)!;
    const rounds = personalRounds(pid, st.status === 'guest');
    if (!rounds.length) continue;
    seen.add(pid);
    players.push({
      player_id: pid, name: fullName(s), first_name: s.first_name, last_name: s.last_name,
      rank: st.rank, status: st.status, order: players.length, rounds,
    });
  }
  // Anyone who played but isn't on the leaderboard is a guest.
  const extras = Array.from(byPlayer.keys()).filter((pid) => !seen.has(pid))
    .map((pid) => nameById.get(pid)!)
    .sort((a, b) => fullName(a).localeCompare(fullName(b)));
  for (const s of extras) {
    const rounds = personalRounds(s.player_id, true);
    if (!rounds.length) continue;
    players.push({
      player_id: s.player_id, name: fullName(s), first_name: s.first_name, last_name: s.last_name,
      rank: null, status: 'guest', order: players.length, rounds,
    });
  }

  const used = new Set(players.flatMap((p) => p.rounds.map((r) => r.course.key)));
  const roundsOn = (key: string) => players.reduce((n, p) => n + p.rounds.filter((r) => r.course.key === key).length, 0);
  const courseList = Array.from(courses.values())
    .filter((c) => used.has(c.key))
    .map((c) => ({ ...c, hasHolePars: c.hole_pars.some((p) => p != null) }))
    .sort((a, b) => roundsOn(b.key) - roundsOn(a.key) || a.label.localeCompare(b.label));
  // Point every player-round at the finalised course object.
  const finalCourse = new Map(courseList.map((c) => [c.key, c]));
  for (const p of players) for (const r of p.rounds) { r.course = finalCourse.get(r.course.key)!; r.pars = r.course.hole_pars; }

  return {
    tournament: data.tournament,
    courses: courseList,
    players,
    maxRounds: players.reduce((m, p) => Math.max(m, p.rounds.length), 0),
  };
}

export const isCompetitor = (p: PlayerModel) => p.status !== 'guest';
const competitorRounds = (model: AnalysisModel) => model.players.filter(isCompetitor).flatMap((p) => p.rounds);

// ── Filters ─────────────────────────────────────────────────────────────────

export interface RoundFilter {
  /** 'all' or a personal round number. */
  round: 'all' | number;
  /** 'all' or a course key. */
  course: 'all' | string;
}

export const ALL_ROUNDS: RoundFilter = { round: 'all', course: 'all' };

export const roundsFor = (p: PlayerModel, f: RoundFilter = ALL_ROUNDS) =>
  p.rounds.filter((r) => (f.round === 'all' || r.index === f.round) && (f.course === 'all' || r.course.key === f.course));

/** A round's total on the chosen metric. */
export const roundTotal = (r: PlayerRound, metric: Metric) => (metric === 'net' ? r.net : r.gross);

/** One hole on the chosen metric: gross, plus an even share of the handicap for net. */
export function holeValue(r: PlayerRound, hole: number, metric: Metric): number | null {
  const v = r.score.holes[hole - 1];
  if (!isNum(v)) return null;
  return metric === 'net' ? v + r.handicap / r.played.length : v;
}

// ── Position trend ──────────────────────────────────────────────────────────

export type Granularity = 'hole' | 'nine' | 'round';

export interface Checkpoint {
  /** Personal round (1-based). */
  round: number;
  /** Holes completed in that round. */
  thru: number;
  holes: number;
  isRoundEnd: boolean;
  /** "R2 · thru 12" / "After R2" */
  label: string;
}

export interface TrendRow {
  player_id: number;
  /** Running score at each checkpoint (see positionTrend); null = no data. */
  values: (number | null)[];
  positions: (number | null)[];
  tied: boolean[];
}

export interface TrendResult {
  checkpoints: Checkpoint[];
  rows: TrendRow[];
  /** Per personal round (index 0 = R1): hole-by-hole available for everyone. */
  holeLevel: boolean[];
  /** Rounds without hole-by-hole places, and whose cards are missing. */
  missing: { round: number; players: string[] }[];
  /** Rounds whose mid-round places use the course par, not hole pars. */
  approxRounds: number[];
}

const PAR_PER_HOLE_FALLBACK = 4;

/**
 * Where everyone stood after every hole.
 *
 * Running score after hole h of round r = completed rounds' official totals
 * + gross through h + par of the holes still to play (+ handicap × h / holes
 * for net). At the last hole of a round it is exactly the official total, so
 * places after each round — and at the finish — match the leaderboard, even
 * when players were on different courses. Mid-round it ranks players by score
 * to par, as a leaderboard would.
 *
 * A round only gets hole-by-hole places when every player in it has a
 * complete card; otherwise it shows its end-of-round places only.
 */
export function positionTrend(model: AnalysisModel, metric: Metric, granularity: Granularity): TrendResult {
  const field = model.players.filter(isCompetitor);
  const checkpoints: Checkpoint[] = [];
  const holeLevel: boolean[] = [];
  const missing: TrendResult['missing'] = [];
  const approxRounds: number[] = [];

  for (let r = 1; r <= model.maxRounds; r++) {
    const inRound = field.map((p) => p.rounds[r - 1]).filter((x): x is PlayerRound => !!x);
    if (!inRound.length) { holeLevel.push(false); continue; }
    const holes = inRound[0].played.length;
    const sameShape = inRound.every((x) => x.played.length === holes);
    const lacking = field.filter((p) => p.rounds[r - 1] && !p.rounds[r - 1].holesComplete).map((p) => p.name);
    const byHole = sameShape && lacking.length === 0;
    holeLevel.push(byHole);
    if (!byHole) missing.push({ round: r, players: lacking });
    if (byHole && inRound.some((x) => x.played.some((h) => x.pars[h - 1] == null))) approxRounds.push(r);

    const thrus = !byHole || granularity === 'round'
      ? [holes]
      : granularity === 'nine'
        ? Array.from(new Set([Math.min(9, holes), holes]))
        : Array.from({ length: holes }, (_, i) => i + 1);
    for (const thru of thrus) {
      const isRoundEnd = thru === holes;
      checkpoints.push({
        round: r, thru, holes, isRoundEnd,
        label: isRoundEnd ? `After R${r}` : `R${r} · thru ${thru}`,
      });
    }
  }

  const valueAt = (p: PlayerModel, cp: Checkpoint): number | null => {
    let base = 0;
    for (let i = 0; i < cp.round - 1; i++) {
      const done = p.rounds[i];
      if (!done) return null;
      base += roundTotal(done, metric);
    }
    const cur = p.rounds[cp.round - 1];
    if (!cur) return null;
    if (cp.isRoundEnd) return base + roundTotal(cur, metric);
    const playedSoFar = cur.played.slice(0, cp.thru);
    const toPlay = cur.played.slice(cp.thru);
    const strokes = sumOf(playedSoFar.map((h) => cur.score.holes[h - 1] as number));
    const coursePerHole = cur.course.par != null ? cur.course.par / 18 : PAR_PER_HOLE_FALLBACK;
    const remainingPar = sumOf(toPlay.map((h) => cur.pars[h - 1] ?? coursePerHole));
    const hcp = metric === 'net' ? (cur.handicap * cp.thru) / cur.played.length : 0;
    return base + strokes + remainingPar + hcp;
  };

  const lastIdx = checkpoints.length - 1;
  const rows: TrendRow[] = field.map((p) => ({ player_id: p.player_id, values: [], positions: [], tied: [] }));
  checkpoints.forEach((cp, ci) => {
    // The finish matches the official result: DQ and DNF players aren't placed.
    const eligible = field.filter((p) => ci !== lastIdx || cp.round < model.maxRounds || p.status === 'ranked');
    const vals = new Map<number, number>();
    for (const p of field) {
      const v = valueAt(p, cp);
      if (v != null && eligible.includes(p)) vals.set(p.player_id, v);
    }
    const sorted = Array.from(vals.entries())
      .map(([player_id, v]) => ({ player_id, total: Math.round(v * 100) / 100 }))
      .sort((a, b) => a.total - b.total);
    const ranked = withCompetitionRanks(sorted);
    const count = new Map<number, number>();
    ranked.forEach((x) => count.set(x.rank, (count.get(x.rank) ?? 0) + 1));
    const byId = new Map(ranked.map((x) => [x.player_id, x]));
    rows.forEach((row) => {
      const hit = byId.get(row.player_id);
      row.values.push(hit ? vals.get(row.player_id)! : null);
      row.positions.push(hit ? hit.rank : null);
      row.tied.push(hit ? (count.get(hit.rank) ?? 0) > 1 : false);
    });
  });

  return { checkpoints, rows, holeLevel, missing, approxRounds };
}

export const placeLabel = (pos: number | null, tied: boolean) => (pos == null ? '—' : `${tied ? 'T' : ''}${pos}`);

export interface TrendStory {
  leadChanges: number;
  /** Checkpoints each player was leading (alone or tied), and out of how many. */
  timeInLead: Map<number, number>;
  checkpointCount: number;
  wireToWire: number | null;
  biggestClimb: { player_id: number; from: number; to: number } | null;
}

export function trendStory(trend: TrendResult): TrendStory {
  let leadChanges = 0;
  let lastSolo: number | null = null;
  const timeInLead = new Map<number, number>();
  trend.checkpoints.forEach((_, ci) => {
    const leaders = trend.rows.filter((r) => r.positions[ci] === 1).map((r) => r.player_id);
    leaders.forEach((pid) => timeInLead.set(pid, (timeInLead.get(pid) ?? 0) + 1));
    if (leaders.length === 1) {
      if (lastSolo != null && leaders[0] !== lastSolo) leadChanges += 1;
      lastSolo = leaders[0];
    }
  });
  const n = trend.checkpoints.length;
  const wire = trend.rows.find((r) => n > 1 && r.positions.every((p, i) => p === 1 && !r.tied[i]));

  let biggestClimb: TrendStory['biggestClimb'] = null;
  if (n > 1) {
    for (const r of trend.rows) {
      const from = r.positions[0];
      const to = r.positions[n - 1];
      if (from == null || to == null) continue;
      if (from - to > 0 && (!biggestClimb || from - to > biggestClimb.from - biggestClimb.to)) {
        biggestClimb = { player_id: r.player_id, from, to };
      }
    }
  }
  return { leadChanges, timeInLead, checkpointCount: n, wireToWire: wire?.player_id ?? null, biggestClimb };
}

// ── Head-to-head ────────────────────────────────────────────────────────────

export interface HeadToHead {
  /** B's running score − A's at each checkpoint: positive = A ahead. */
  gap: (number | null)[];
  checkpoints: Checkpoint[];
  /** Holes both played on the same course in the same personal round (gross). */
  holes: { round: number; hole: number; course: string; a: number; b: number }[];
  aWon: number;
  bWon: number;
  halved: number;
  biggestSwing: { round: number; hole: number; course: string; by: number; winner: 'a' | 'b' } | null;
}

export function headToHead(model: AnalysisModel, trend: TrendResult, aId: number, bId: number): HeadToHead {
  const ra = trend.rows.find((r) => r.player_id === aId);
  const rb = trend.rows.find((r) => r.player_id === bId);
  const gap = trend.checkpoints.map((_, i) => {
    const a = ra?.values[i];
    const b = rb?.values[i];
    return a != null && b != null ? b - a : null;
  });
  const pa = model.players.find((p) => p.player_id === aId);
  const pb = model.players.find((p) => p.player_id === bId);
  const holes: HeadToHead['holes'] = [];
  if (pa && pb) {
    for (let i = 0; i < Math.min(pa.rounds.length, pb.rounds.length); i++) {
      const x = pa.rounds[i];
      const y = pb.rounds[i];
      if (x.course.key !== y.course.key) continue;
      for (const h of x.played) {
        const a = x.score.holes[h - 1];
        const b = y.score.holes[h - 1];
        if (isNum(a) && isNum(b) && y.played.includes(h)) holes.push({ round: i + 1, hole: h, course: x.course.label, a, b });
      }
    }
  }
  let aWon = 0, bWon = 0, halved = 0;
  let biggestSwing: HeadToHead['biggestSwing'] = null;
  for (const h of holes) {
    if (h.a < h.b) aWon += 1; else if (h.b < h.a) bWon += 1; else halved += 1;
    const by = Math.abs(h.a - h.b);
    if (by > 0 && (!biggestSwing || by > biggestSwing.by)) {
      biggestSwing = { round: h.round, hole: h.hole, course: h.course, by, winner: h.a < h.b ? 'a' : 'b' };
    }
  }
  return { gap, checkpoints: trend.checkpoints, holes, aWon, bWon, halved, biggestSwing };
}

// ── Courses ─────────────────────────────────────────────────────────────────

export interface CourseCell { n: number; avg: number; best: number; vsField: number | null }

export interface CourseSummary {
  course: CourseInfo;
  playerRounds: number;
  matches: number;
  fieldAvg: number | null;
  fieldAvgGross: number | null;
  /** Field gross average vs par. */
  vsPar: number | null;
  low: { player_id: number; value: number; round: number; date: string } | null;
  high: { player_id: number; value: number; round: number; date: string } | null;
}

export interface CoursePlayerRow {
  player_id: number;
  cells: Record<string, CourseCell>;
  overall: number | null;
  rounds: number;
  /** Course with the biggest gain on the field (needs ≥ 2 courses). */
  bestCourse: string | null;
}

const avg = (vals: number[]) => (vals.length ? sumOf(vals) / vals.length : null);

export function coursePerformance(model: AnalysisModel, metric: Metric, f: RoundFilter = ALL_ROUNDS) {
  const field = competitorRounds(model);
  const summaries: CourseSummary[] = model.courses.map((course) => {
    const on = field.filter((r) => r.course.key === course.key);
    const vals = on.map((r) => roundTotal(r, metric));
    const grossVals = on.map((r) => r.gross);
    const pick = (better: (a: number, b: number) => boolean) => on.reduce<CourseSummary['low']>((best, r) => {
      const v = roundTotal(r, metric);
      return !best || better(v, best.value) ? { player_id: r.player_id, value: v, round: r.index, date: r.round.date } : best;
    }, null);
    const fieldAvgGross = avg(grossVals);
    const fullRounds = on.filter((r) => r.played.length === 18);
    const gross18 = avg(fullRounds.map((r) => r.gross));
    return {
      course,
      playerRounds: on.length,
      matches: new Set(on.map((r) => r.round.round_id)).size,
      fieldAvg: avg(vals),
      fieldAvgGross,
      vsPar: course.par != null && gross18 != null ? gross18 - course.par : null,
      low: pick((a, b) => a < b),
      high: pick((a, b) => a > b),
    };
  });
  const fieldAvgBy = new Map(summaries.map((s) => [s.course.key, s.fieldAvg]));

  const rows: CoursePlayerRow[] = model.players.map((p) => {
    const mine = roundsFor(p, { round: f.round, course: 'all' });
    const cells: Record<string, CourseCell> = {};
    for (const c of model.courses) {
      const on = mine.filter((r) => r.course.key === c.key).map((r) => roundTotal(r, metric));
      if (!on.length) continue;
      const a = sumOf(on) / on.length;
      const fa = fieldAvgBy.get(c.key);
      cells[c.key] = { n: on.length, avg: a, best: Math.min(...on), vsField: fa != null ? a - fa : null };
    }
    const entries = Object.entries(cells).filter(([, c]) => c.vsField != null);
    const best = entries.length >= 2 ? entries.reduce((b, e) => ((e[1].vsField as number) < (b[1].vsField as number) ? e : b)) : null;
    const all = mine.map((r) => roundTotal(r, metric));
    return { player_id: p.player_id, cells, overall: avg(all), rounds: all.length, bestCourse: best ? best[0] : null };
  });
  return { summaries, rows };
}

// ── Hole by hole: field averages and strokes gained ─────────────────────────

export interface HoleFieldStat {
  hole: number;
  par: number | null;
  n: number;
  /** Field average on this hole (metric). */
  avg: number | null;
  /** Field gross average minus par. */
  avgToPar: number | null;
  /** 1 = hardest on the course (by gross average vs par, else raw average). */
  difficulty: number | null;
}

/** Field averages for every hole of a course, from every competing round played on it. */
export function holeFieldStats(model: AnalysisModel, courseKey: string, metric: Metric): HoleFieldStat[] {
  const course = model.courses.find((c) => c.key === courseKey);
  const on = competitorRounds(model).filter((r) => r.course.key === courseKey);
  const stats: HoleFieldStat[] = Array.from({ length: 18 }, (_, i) => {
    const hole = i + 1;
    const vals: number[] = [];
    const gross: number[] = [];
    for (const r of on) {
      if (!r.played.includes(hole)) continue;
      const v = holeValue(r, hole, metric);
      if (v == null) continue;
      vals.push(v);
      gross.push(r.score.holes[i] as number);
    }
    const par = course?.hole_pars[i] ?? null;
    const g = avg(gross);
    return { hole, par, n: vals.length, avg: avg(vals), avgToPar: g != null && par != null ? g - par : null, difficulty: null };
  });
  const rankable = stats.filter((s) => s.n > 0);
  const hardness = (s: HoleFieldStat) => (s.avgToPar != null ? s.avgToPar : (s.avg ?? 0) - 4);
  [...rankable].sort((a, b) => hardness(b) - hardness(a)).forEach((s, i) => { s.difficulty = i + 1; });
  return stats;
}

export type Baseline = 'field' | 'par';

export interface SgCell { sum: number; n: number }

export interface HoleSgRow {
  player_id: number;
  /** Per hole 1–18: strokes gained summed over the rounds played there. */
  holes: (SgCell | null)[];
  out: SgCell | null;
  in: SgCell | null;
  total: SgCell | null;
  rounds: number;
}

const addCell = (c: SgCell | null, v: number): SgCell => (c ? { sum: c.sum + v, n: c.n + 1 } : { sum: v, n: 1 });

/**
 * Strokes gained on each hole of one course. vs field = the field's average on
 * the hole − your score; vs par = par − your score. Positive is good.
 */
export function strokesGainedByHole(
  model: AnalysisModel, courseKey: string, baseline: Baseline, metric: Metric, f: RoundFilter = ALL_ROUNDS,
): { holes: HoleFieldStat[]; rows: HoleSgRow[] } {
  const holes = holeFieldStats(model, courseKey, metric);
  const rows = model.players.map((p) => {
    const mine = roundsFor(p, { round: f.round, course: courseKey });
    const cells: (SgCell | null)[] = new Array(18).fill(null);
    let out: SgCell | null = null;
    let inn: SgCell | null = null;
    let total: SgCell | null = null;
    let rounds = 0;
    for (const r of mine) {
      let any = false;
      for (const h of r.played) {
        const v = holeValue(r, h, metric);
        if (v == null) continue;
        const stat = holes[h - 1];
        // vs par on net: par is the target for the handicap-adjusted score.
        const ref = baseline === 'field' ? stat.avg : stat.par;
        if (ref == null) continue;
        const sg = ref - v;
        cells[h - 1] = addCell(cells[h - 1], sg);
        if (h <= 9) out = addCell(out, sg); else inn = addCell(inn, sg);
        total = addCell(total, sg);
        any = true;
      }
      if (any) rounds += 1;
    }
    return { player_id: p.player_id, holes: cells, out, in: inn, total, rounds };
  });
  return { holes, rows };
}

export interface ParTypeRow {
  player_id: number;
  par3: SgCell | null;
  par4: SgCell | null;
  par5: SgCell | null;
  front: SgCell | null;
  back: SgCell | null;
  total: SgCell | null;
}

/** Across every course: strokes gained vs the field, grouped by par and nine. */
export function strokesGainedByParType(model: AnalysisModel, baseline: Baseline, metric: Metric, f: RoundFilter = ALL_ROUNDS): ParTypeRow[] {
  const statsBy = new Map(model.courses.map((c) => [c.key, holeFieldStats(model, c.key, metric)]));
  return model.players.map((p) => {
    const row: ParTypeRow = { player_id: p.player_id, par3: null, par4: null, par5: null, front: null, back: null, total: null };
    for (const r of roundsFor(p, f)) {
      const stats = statsBy.get(r.course.key)!;
      for (const h of r.played) {
        const v = holeValue(r, h, metric);
        const stat = stats[h - 1];
        const ref = baseline === 'field' ? stat.avg : stat.par;
        if (v == null || ref == null) continue;
        const sg = ref - v;
        if (stat.par === 3) row.par3 = addCell(row.par3, sg);
        else if (stat.par === 4) row.par4 = addCell(row.par4, sg);
        else if (stat.par != null && stat.par >= 5) row.par5 = addCell(row.par5, sg);
        if (h <= 9) row.front = addCell(row.front, sg); else row.back = addCell(row.back, sg);
        row.total = addCell(row.total, sg);
      }
    }
    return row;
  });
}

// ── Scoring mix (gross) ─────────────────────────────────────────────────────

export type MixKind = 'eagle' | 'birdie' | 'par' | 'bogey' | 'double' | 'triple';
export const MIX_KINDS: MixKind[] = ['eagle', 'birdie', 'par', 'bogey', 'double', 'triple'];
export const MIX_LABEL: Record<MixKind, string> = {
  eagle: 'Eagle+', birdie: 'Birdie', par: 'Par', bogey: 'Bogey', double: 'Double', triple: 'Triple+',
};

export interface ScoringRow {
  player_id: number;
  holes: number;
  counts: Record<MixKind, number>;
  parOrBetter: number;
  byPar: Record<3 | 4 | 5, { toPar: number; n: number } | null>;
  bounceBack: { made: number; chances: number };
  longestStreak: number;
  /** Holes played but left out because the hole has no par. */
  noPar: number;
}

const mixKindOf = (strokes: number, par: number): MixKind => {
  const k = scoreKind(strokes, par);
  if (k === 'albatross' || k === 'eagle') return 'eagle';
  if (k === 'worse') return 'triple';
  return (k ?? 'par') as MixKind;
};

export function scoringMix(model: AnalysisModel, f: RoundFilter = ALL_ROUNDS): ScoringRow[] {
  return model.players.map((p) => {
    const counts: Record<MixKind, number> = { eagle: 0, birdie: 0, par: 0, bogey: 0, double: 0, triple: 0 };
    const byParAcc: Record<3 | 4 | 5, { sum: number; n: number }> = { 3: { sum: 0, n: 0 }, 4: { sum: 0, n: 0 }, 5: { sum: 0, n: 0 } };
    let holes = 0, noPar = 0, streak = 0, longest = 0, made = 0, chances = 0;
    for (const r of roundsFor(p, f)) {
      let prevOver = false;
      for (const h of r.played) {
        const v = r.score.holes[h - 1];
        if (!isNum(v)) { prevOver = false; continue; }
        const par = r.pars[h - 1];
        if (par == null) { noPar += 1; prevOver = false; streak = 0; continue; }
        holes += 1;
        counts[mixKindOf(v, par)] += 1;
        const bucket = (par >= 5 ? 5 : par <= 3 ? 3 : 4) as 3 | 4 | 5;
        byParAcc[bucket].sum += v - par;
        byParAcc[bucket].n += 1;
        if (prevOver) { chances += 1; if (v <= par) made += 1; }
        prevOver = v > par;
        streak = v <= par ? streak + 1 : 0;
        longest = Math.max(longest, streak);
      }
    }
    const byPar = ([3, 4, 5] as const).reduce((acc, k) => {
      acc[k] = byParAcc[k].n ? { toPar: byParAcc[k].sum / byParAcc[k].n, n: byParAcc[k].n } : null;
      return acc;
    }, {} as ScoringRow['byPar']);
    return {
      player_id: p.player_id, holes, counts,
      parOrBetter: counts.eagle + counts.birdie + counts.par,
      byPar, bounceBack: { made, chances }, longestStreak: longest, noPar,
    };
  });
}

// ── Putting (gross, from scorecard putts) ───────────────────────────────────

export interface PuttingRow {
  player_id: number;
  holes: number;
  putts: number;
  per18: number | null;
  dist: [number, number, number, number]; // 0, 1, 2, 3+ putts
  gir: { hit: number; n: number };
  puttsOnGir: number;
  scrambling: { made: number; chances: number };
}

export function puttingStats(model: AnalysisModel, f: RoundFilter = ALL_ROUNDS): PuttingRow[] {
  return model.players.map((p) => {
    let holes = 0, putts = 0, hit = 0, girN = 0, puttsOnGir = 0, made = 0, chances = 0;
    const dist: [number, number, number, number] = [0, 0, 0, 0];
    for (const r of roundsFor(p, f)) {
      for (const h of r.played) {
        const pt = r.score.putts[h - 1];
        const v = r.score.holes[h - 1];
        if (!isNum(pt) || !isNum(v)) continue;
        holes += 1;
        putts += pt;
        dist[Math.min(pt, 3)] += 1;
        const par = r.pars[h - 1];
        if (par == null) continue;
        girN += 1;
        if (v - pt <= par - 2) { hit += 1; puttsOnGir += pt; }
        else { chances += 1; if (v <= par) made += 1; }
      }
    }
    return {
      player_id: p.player_id, holes, putts, per18: holes ? (putts / holes) * 18 : null, dist,
      gir: { hit, n: girN }, puttsOnGir, scrambling: { made, chances },
    };
  });
}

// ── Penalties ───────────────────────────────────────────────────────────────

export interface LeaguePenalty { player_id: number; round: number; course: string; date: string; strokes: number; reason: string | null }
export interface Disqualification { player_id: number; round: number; course: string; date: string; reason: string | null }
export interface CardPenaltyRow {
  player_id: number;
  roundsWithData: number;
  holesWithData: number;
  strokes: number;
  holesWithPenalty: number;
  per18: number | null;
  /** Strokes lost to the field (gross) on the holes where a penalty happened. */
  lostOnThoseHoles: number | null;
}
export interface HotSpot { course: string; courseKey: string; hole: number; strokes: number; players: number }

export function penaltyStats(model: AnalysisModel, f: RoundFilter = ALL_ROUNDS) {
  const league: LeaguePenalty[] = [];
  const dqs: Disqualification[] = [];
  const fieldBy = new Map(model.courses.map((c) => [c.key, holeFieldStats(model, c.key, 'gross')]));
  const spots = new Map<string, HotSpot & { who: Set<number> }>();

  const cards: CardPenaltyRow[] = model.players.map((p) => {
    let roundsWithData = 0, holesWithData = 0, strokes = 0, holesWithPenalty = 0, lost = 0, lostN = 0;
    for (const r of roundsFor(p, f)) {
      if (r.score.penalty_strokes > 0) {
        league.push({ player_id: p.player_id, round: r.index, course: r.course.label, date: r.round.date, strokes: r.score.penalty_strokes, reason: r.score.penalty_reason });
      }
      if (r.score.is_disqualified) {
        dqs.push({ player_id: p.player_id, round: r.index, course: r.course.label, date: r.round.date, reason: r.score.dq_reason });
      }
      let any = false;
      for (const h of r.played) {
        const pen = r.score.penalties[h - 1];
        if (!isNum(pen)) continue;
        any = true;
        holesWithData += 1;
        if (pen <= 0) continue;
        strokes += pen;
        holesWithPenalty += 1;
        const v = r.score.holes[h - 1];
        const fa = fieldBy.get(r.course.key)?.[h - 1].avg;
        if (isNum(v) && fa != null) { lost += v - fa; lostN += 1; }
        if (isCompetitor(p)) {
          const key = `${r.course.key}:${h}`;
          const spot = spots.get(key) ?? { course: r.course.label, courseKey: r.course.key, hole: h, strokes: 0, players: 0, who: new Set<number>() };
          spot.strokes += pen;
          spot.who.add(p.player_id);
          spot.players = spot.who.size;
          spots.set(key, spot);
        }
      }
      if (any) roundsWithData += 1;
    }
    return {
      player_id: p.player_id, roundsWithData, holesWithData, strokes, holesWithPenalty,
      per18: holesWithData ? (strokes / holesWithData) * 18 : null,
      lostOnThoseHoles: lostN ? lost : null,
    };
  });
  const hotSpots: HotSpot[] = Array.from(spots.values())
    .map(({ who, ...s }) => s)
    .sort((a, b) => b.strokes - a.strokes || b.players - a.players || a.hole - b.hole);
  return { league, dqs, cards, hotSpots };
}

// ── Missing data: exactly what to add, and where ────────────────────────────

export interface MissingPlayer { player_id: number; name: string; kind: 'missing' | 'partial' | 'mismatch'; detail?: string }
export interface MissingMatch { round_id: number; date: string; course: string; players: MissingPlayer[] }

export interface MissingData {
  teeboxesWithoutPars: { courseKey: string; label: string; par: number | null; rounds: number }[];
  /** Tee boxes with some but not all hole pars. */
  teeboxesPartialPars: { courseKey: string; label: string; missingHoles: number[] }[];
  holeScores: MissingMatch[];
  putts: MissingMatch[];
  penalties: MissingMatch[];
  counts: {
    teeboxes: { ok: number; total: number };
    holes: { ok: number; total: number };
    putts: { ok: number; total: number };
    penalties: { ok: number; total: number };
  };
  anything: boolean;
}

/**
 * What the analysis still needs, per tee box and per match. Only competitors
 * are ever asked for — a guest's missing card blocks nothing.
 */
export function missingData(model: AnalysisModel): MissingData {
  const rounds = competitorRounds(model);
  const nameOf = new Map(model.players.map((p) => [p.player_id, p.name]));
  const byMatch = (pick: (r: PlayerRound) => MissingPlayer | null): MissingMatch[] => {
    const out = new Map<number, MissingMatch>();
    for (const r of rounds) {
      const m = pick(r);
      if (!m) continue;
      const entry = out.get(r.round.round_id) ?? { round_id: r.round.round_id, date: r.round.date, course: r.course.label, players: [] };
      entry.players.push(m);
      out.set(r.round.round_id, entry);
    }
    return Array.from(out.values()).sort((a, b) => a.date.localeCompare(b.date) || a.round_id - b.round_id);
  };
  const who = (r: PlayerRound, kind: MissingPlayer['kind'], detail?: string): MissingPlayer =>
    ({ player_id: r.player_id, name: nameOf.get(r.player_id) ?? '', kind, ...(detail ? { detail } : {}) });

  const holeScores = byMatch((r) => {
    if (r.holesMismatch) return who(r, 'mismatch', `holes add to ${r.holesSum}, gross is ${r.gross}`);
    if (r.holesComplete) return null;
    return r.holesFilled > 0 ? who(r, 'partial', `${r.holesFilled} of ${r.played.length} holes`) : who(r, 'missing');
  });
  const putts = byMatch((r) => (r.hasPutts ? null : who(r, 'missing')));
  const penalties = byMatch((r) => (r.hasPenalties ? null : who(r, 'missing')));

  const usedCourses = model.courses.filter((c) => rounds.some((r) => r.course.key === c.key));
  const roundsOn = (key: string) => rounds.filter((r) => r.course.key === key).length;
  const teeboxesWithoutPars = usedCourses.filter((c) => !c.hasHolePars)
    .map((c) => ({ courseKey: c.key, label: c.label, par: c.par, rounds: roundsOn(c.key) }));
  const teeboxesPartialPars = usedCourses.filter((c) => c.hasHolePars).map((c) => {
    const playedHoleSet = new Set(rounds.filter((r) => r.course.key === c.key).flatMap((r) => r.played));
    return { courseKey: c.key, label: c.label, missingHoles: Array.from(playedHoleSet).filter((h) => c.hole_pars[h - 1] == null).sort((a, b) => a - b) };
  }).filter((c) => c.missingHoles.length > 0);

  const total = rounds.length;
  const counts = {
    teeboxes: { ok: usedCourses.length - teeboxesWithoutPars.length - teeboxesPartialPars.length, total: usedCourses.length },
    holes: { ok: rounds.filter((r) => r.holesComplete && !r.holesMismatch).length, total },
    putts: { ok: rounds.filter((r) => r.hasPutts).length, total },
    penalties: { ok: rounds.filter((r) => r.hasPenalties).length, total },
  };
  return {
    teeboxesWithoutPars,
    teeboxesPartialPars,
    holeScores,
    putts,
    penalties,
    counts,
    anything: teeboxesWithoutPars.length + teeboxesPartialPars.length + holeScores.length + putts.length + penalties.length > 0,
  };
}

// ── Highlights ──────────────────────────────────────────────────────────────

export interface Highlight { key: string; label: string; value: string; detail?: string }

const fmtNum = (v: number, digits = 1) => (Number.isInteger(v) ? String(v) : v.toFixed(digits));
export const fmtSigned = (v: number | null | undefined, digits = 1) => {
  if (v == null) return '—';
  const r = Math.round(v * 10 ** digits) / 10 ** digits;
  if (r === 0) return digits ? (0).toFixed(digits) : '0';
  return `${r > 0 ? '+' : ''}${r.toFixed(digits)}`;
};
export const fmtToPar = (v: number | null | undefined, digits = 1) => {
  if (v == null) return '—';
  const r = Math.round(v * 10 ** digits) / 10 ** digits;
  if (r === 0) return 'E';
  return `${r > 0 ? '+' : ''}${fmtNum(r, digits)}`;
};

export function highlights(model: AnalysisModel): Highlight[] {
  const out: Highlight[] = [];
  const nameOf = (id: number) => model.players.find((p) => p.player_id === id)?.first_name ?? '';
  const competitors = model.players.filter(isCompetitor);

  // Winner and margin (net, as the major is decided).
  const ranked = competitors.filter((p) => p.status === 'ranked' && p.rank != null);
  const winners = ranked.filter((p) => p.rank === 1);
  if (winners.length) {
    const total = (p: PlayerModel) => sumOf(p.rounds.map((r) => r.net));
    const next = ranked.find((p) => (p.rank ?? 0) > 1);
    const margin = next ? total(next) - total(winners[0]) : null;
    out.push({
      key: 'winner',
      label: winners.length > 1 ? 'Co-champions' : 'Champion',
      value: winners.map((w) => w.first_name).join(' & '),
      detail: winners.length === 1 && margin != null && margin > 0 ? `by ${fmtNum(margin)} stroke${margin === 1 ? '' : 's'}` : `${fmtNum(total(winners[0]))} net`,
    });
  }

  const all = competitors.flatMap((p) => p.rounds);
  const low = (metric: Metric) => all.reduce<PlayerRound | null>((b, r) => (!b || roundTotal(r, metric) < roundTotal(b, metric) ? r : b), null);
  const lowNet = low('net');
  if (lowNet) out.push({ key: 'lowNet', label: 'Low round (net)', value: String(lowNet.net), detail: `${nameOf(lowNet.player_id)} · R${lowNet.index} · ${lowNet.course.course_name}` });
  const lowGross = low('gross');
  if (lowGross) out.push({ key: 'lowGross', label: 'Low round (gross)', value: String(lowGross.gross), detail: `${nameOf(lowGross.player_id)} · R${lowGross.index} · ${lowGross.course.course_name}` });

  const byRound = trendStory(positionTrend(model, 'net', 'round'));
  if (byRound.biggestClimb) {
    const c = byRound.biggestClimb;
    out.push({ key: 'climb', label: 'Biggest climb', value: nameOf(c.player_id), detail: `${ordinal(c.from)} after R1 → ${ordinal(c.to)}` });
  }
  const holeTrend = positionTrend(model, 'net', 'hole');
  const story = holeTrend.holeLevel.some(Boolean) ? trendStory(holeTrend) : byRound;
  if (story.checkpointCount > 1) {
    if (story.wireToWire != null) {
      out.push({ key: 'lead', label: 'Wire to wire', value: nameOf(story.wireToWire), detail: 'led alone at every checkpoint' });
    } else {
      const leader = Array.from(story.timeInLead.entries()).sort((a, b) => b[1] - a[1])[0];
      out.push({
        key: 'lead', label: 'Lead changes', value: String(story.leadChanges),
        detail: leader ? `${nameOf(leader[0])} led ${leader[1]} of ${story.checkpointCount} ${holeTrend.holeLevel.some(Boolean) ? 'checkpoints' : 'rounds'}` : undefined,
      });
    }
  }

  // Hardest / easiest hole: field gross average vs par, holes with ≥ 2 scores.
  const holes = model.courses.flatMap((c) => holeFieldStats(model, c.key, 'gross')
    .filter((s) => s.avgToPar != null && s.n >= 2)
    .map((s) => ({ ...s, course: c })));
  if (holes.length >= 2) {
    const hardest = holes.reduce((b, h) => ((h.avgToPar as number) > (b.avgToPar as number) ? h : b));
    const easiest = holes.reduce((b, h) => ((h.avgToPar as number) < (b.avgToPar as number) ? h : b));
    out.push({ key: 'hardest', label: 'Hardest hole', value: `${hardest.course.course_name} #${hardest.hole}`, detail: `par ${hardest.par} · field ${fmtToPar(hardest.avgToPar)}` });
    out.push({ key: 'easiest', label: 'Easiest hole', value: `${easiest.course.course_name} #${easiest.hole}`, detail: `par ${easiest.par} · field ${fmtToPar(easiest.avgToPar)}` });
  }

  const mix = scoringMix(model).filter((r) => competitors.some((p) => p.player_id === r.player_id));
  const birdieLeader = mix.reduce<ScoringRow | null>((b, r) => ((r.counts.birdie + r.counts.eagle) > ((b?.counts.birdie ?? 0) + (b?.counts.eagle ?? 0)) ? r : b), null);
  if (birdieLeader) {
    const n = birdieLeader.counts.birdie + birdieLeader.counts.eagle;
    out.push({ key: 'birdies', label: 'Most birdies', value: nameOf(birdieLeader.player_id), detail: `${n} birdie${n === 1 ? '' : 's'} or better` });
  }

  const putting = puttingStats(model).filter((r) => r.holes >= 9 && competitors.some((p) => p.player_id === r.player_id));
  if (putting.length) {
    const best = putting.reduce((b, r) => ((r.per18 as number) < (b.per18 as number) ? r : b));
    out.push({ key: 'putter', label: 'Best putter', value: nameOf(best.player_id), detail: `${fmtNum(best.per18 as number)} putts per 18` });
  }
  return out;
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}
